"use client"

import { XIcon } from "lucide-react"
import * as React from "react"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { cn } from "@/lib/utils"

import { statusIcons } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import { floatingSurfaceVariants } from "@/components/ui/popover"
import { Spinner } from "@/components/ui/spinner"

// Follows the `.dark` class on <html> instead of next-themes.
function subscribeToDarkClass(onChange: () => void) {
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  })
  return () => observer.disconnect()
}

function useDarkClassTheme(): ToasterProps["theme"] {
  return React.useSyncExternalStore(
    subscribeToDarkClass,
    () =>
      document.documentElement.classList.contains("dark") ? "dark" : "light",
    () => "light",
  )
}

// Toasts are unstyled so Tailwind classes, not Sonner's own CSS, decide the
// look. The surface stays neutral; each status sets its border and icon
// colour, and the icon carries the meaning for anyone who cannot see colour.
const toastClassNames: NonNullable<ToasterProps["toastOptions"]>["classNames"] =
  {
    // A toast is a container, so it takes the lg radius (Brand.mdx, Radius).
    toast: cn(
      floatingSurfaceVariants({ kind: "panel", motion: "none" }),
      "flex w-(--width) items-start gap-2.5 font-sans data-[type=error]:border-destructive-border data-[type=error]:*:data-icon:text-destructive data-[type=info]:border-info-border data-[type=info]:*:data-icon:text-info data-[type=success]:border-success-border data-[type=success]:*:data-icon:text-success data-[type=warning]:border-warning-border data-[type=warning]:*:data-icon:text-warning",
    ),
    content: "flex min-w-0 flex-1 flex-col gap-0.5",
    title: "font-medium text-foreground",
    description: "text-meta text-muted-foreground",
    icon: "relative mt-px flex size-4 shrink-0 items-center justify-center text-muted-foreground",
    actionButton: cn(
      buttonVariants({ variant: "outline", size: "sm" }),
      "shrink-0 self-center",
    ),
    cancelButton: cn(
      buttonVariants({ variant: "ghost", size: "sm" }),
      "shrink-0 self-center",
    ),
    // Sonner's own [data-theme=dark] close button CSS still applies with
    // `unstyled`, so the ghost look is forced.
    closeButton: cn(
      buttonVariants({ variant: "ghost", size: "icon-sm" }),
      "order-last -mt-1 -mr-1.5 shrink-0 border-transparent! bg-transparent! text-muted-foreground! hover:bg-secondary-hover! hover:text-foreground!",
    ),
  }

const Toaster = ({ theme, toastOptions, icons, ...props }: ToasterProps) => {
  const classTheme = useDarkClassTheme()

  return (
    <Sonner
      theme={theme ?? classTheme}
      className="toaster group"
      icons={{
        success: <statusIcons.success className="size-4" />,
        info: <statusIcons.info className="size-4" />,
        warning: <statusIcons.warning className="size-4" />,
        error: <statusIcons.destructive className="size-4" />,
        loading: (
          <Spinner
            size="md"
            role={undefined}
            aria-label={undefined}
            aria-hidden
          />
        ),
        close: <XIcon className="size-4" />,
        ...icons,
      }}
      toastOptions={{
        unstyled: true,
        ...toastOptions,
        classNames: { ...toastClassNames, ...toastOptions?.classNames },
      }}
      style={
        {
          "--width": "22rem",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
